import React from 'react';
import { Box, Button, Dialog, Grid, IconButton, makeStyles, Typography, useMediaQuery, useTheme, Snackbar } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import KioskCheckout from '../KioskCheckout';

const useStyles = makeStyles((theme) => ({
  cartContainer: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    boxShadow: theme.shadows[1],
    position: 'sticky',
    top: theme.spacing(2),
    maxWidth: '100%',
  },
  cartItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  itemDetails: {
    flex: 1,
    marginRight: theme.spacing(2),
  },
  itemName: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  itemDescription: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  itemPrice: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
  },
  cartFooter: {
    marginTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
  },
  checkoutButton: {
    width: '100%',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    fontSize: '1rem',
    fontWeight: 'bold',
  },
}));

interface Modifier {
  id: string;
  name: string;
  price: number;
  tax: number;
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  modifier_assignments: { modifier: Modifier }[];
}

interface CartItem {
  cartKey: string;
  productId: string;
  modifiersKey: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
}

interface CartProps {
  cart: { [key: string]: number };
  dispatch: React.Dispatch<Action>;
  menuData: any;
  totalPrice: number;
  orderDetails: OrderDetails;
  location_id: string;
  organisation_id: string;
  onOrderCompleted?: () => void;
}

type Action =
  | { type: 'ADD_TO_CART'; payload: { cartKey: string; quantity: number } }
  | {
      type: 'REMOVE_FROM_CART';
      payload: { cartKey: string };
    };

interface OrderDetails {
  orderType: 'delivery' | 'pickup' | 'dinein' | null;
  timeOption: 'standard' | 'schedule';
  scheduledDay: string;
  scheduledTime: string;
  tableNumber: number | null;
}

const KioskCart: React.FC<CartProps> = ({ cart, dispatch, menuData, totalPrice, orderDetails, location_id, organisation_id, onOrderCompleted }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [checkoutOpen, setCheckoutOpen] = React.useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const handleOpenCheckout = () => {
    setCheckoutOpen(true);
  };

  const handleCloseCheckout = () => {
    setCheckoutOpen(false);
  };

  // Function to generate cart items with detailed information
  const cartItems: CartItem[] = React.useMemo(() => {
    return Object.keys(cart)
      .map((cartKey) => {
        const quantity = cart[cartKey];
        const [productId, modifiersKey] = cartKey.split(':');
        const product: MenuItem | undefined = menuData?.categories
          .flatMap((category: any) =>
            category.category_type === 'DISH' ? category.dish_assignments.map((item: any) => item.dish) : category.drink_assignments.map((item: any) => item.drink)
          )
          .find((item: any) => item.id === productId);

        if (!product) {
          return null;
        }

        // Parse modifiersKey into an array of modifier names
        const modifiersArray = modifiersKey ? modifiersKey.split(',') : [];

        // Find the selected modifiers by matching modifier names
        const selectedModifiers = product.modifier_assignments.filter((mod) => modifiersArray.includes(mod.modifier.name)).map((mod) => mod.modifier);

        const itemPrice = selectedModifiers.reduce((sum: number, mod: Modifier) => sum + mod.price, product.price) / 100;

        const modifiersDescription = selectedModifiers.map((mod) => mod.name).join(', ');

        return {
          cartKey,
          productId,
          modifiersKey,
          name: product.name,
          description: modifiersDescription,
          price: itemPrice,
          quantity,
        };
      })
      .filter((item): item is CartItem => item !== null);
  }, [cart, menuData]);

  const checkStockAvailability = (cartKey: string, quantityToAdd: number): boolean => {
    const [productId] = cartKey.split(':');
    const product = menuData?.categories
      .flatMap((category: any) =>
        category.category_type === 'DISH' ? category.dish_assignments.map((item: any) => item.dish) : category.drink_assignments.map((item: any) => item.drink)
      )
      .find((item: any) => item.id === productId);
    if (!product || typeof product.item_per_day_left !== 'number') return true;
    // Items with stock tracking disabled are always available.
    if (product.track_stock === false) return true;
    const itemInCartQuantity = Object.keys(cart).reduce((total, key) => {
      const [keyProductId] = key.split(':');
      if (keyProductId === productId) {
        return total + cart[key];
      }
      return total;
    }, 0);
    const totalQuantity = itemInCartQuantity + quantityToAdd;
    return totalQuantity <= product.item_per_day_left;
  };

  const handleAdd = (cartKey: string) => {
    if (checkStockAvailability(cartKey, 1)) {
      dispatch({ type: 'ADD_TO_CART', payload: { cartKey, quantity: 1 } });
    } else {
      setSnackbarOpen(true);
    }
  };

  const handleRemove = (cartKey: string) => {
    if (cart[cartKey] > 1) {
      dispatch({ type: 'ADD_TO_CART', payload: { cartKey, quantity: -1 } });
    } else {
      dispatch({ type: 'REMOVE_FROM_CART', payload: { cartKey } });
    }
  };

  if (isMobile) {
    return (
      <>
        <Box
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            ...(menuData?.location_info?.ordering_system_primary_color ? { backgroundColor: menuData.location_info.ordering_system_primary_color } : {}),
            ...(menuData?.location_info?.ordering_system_secondary_color ? { color: menuData.location_info.ordering_system_secondary_color } : {}),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(1, 2),
            zIndex: 1000,
          }}
          onClick={handleOpenCheckout}>
          <Typography variant="body1">{cartItems.length} items</Typography>
          <Typography variant="body1">Total: £{totalPrice.toFixed(2)}</Typography>
        </Box>
        {/* Checkout Modal */}
        <Dialog open={checkoutOpen} onClose={handleCloseCheckout} fullWidth fullScreen maxWidth="sm" aria-labelledby="checkout-dialog" disableScrollLock>
          {/*@ts-ignore*/}
          <KioskCheckout
            //@ts-ignore
            orderDetails={orderDetails}
            //@ts-ignore
            dispatch={dispatch}
            totalPrice={totalPrice}
            onClose={handleCloseCheckout}
            cartItems={cartItems}
            cart={cart}
            menuData={menuData}
            location_id={location_id}
            organisation_id={organisation_id}
            onOrderCompleted={onOrderCompleted}
          />
        </Dialog>
      </>
    );
  }

  return (
    <Box className={classes.cartContainer}>
      {cartItems.length === 0 ? (
        <Typography variant="body1">Your cart is empty</Typography>
      ) : (
        <>
          {cartItems.map((item) => (
            <Grid key={item.cartKey} container className={classes.cartItem}>
              <Grid item className={classes.itemDetails}>
                <Typography className={classes.itemName}>{item.name}</Typography>
                {item.description && <Typography className={classes.itemDescription}>{item.description}</Typography>}
                <Box className={classes.quantityControls}>
                  <IconButton size="small" onClick={() => handleRemove(item.cartKey)}>
                    {item.quantity > 1 ? <RemoveIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                  <Typography variant="body2">{item.quantity}</Typography>
                  <IconButton size="small" onClick={() => handleAdd(item.cartKey)}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
              <Grid item>
                <Typography className={classes.itemPrice}>£{(item.price * item.quantity).toFixed(2)}</Typography>
              </Grid>
            </Grid>
          ))}
          <Box className={classes.cartFooter}>
            <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: theme.spacing(1) }}>
              Total: £{totalPrice.toFixed(2)}
            </Typography>
            <Button
              className={classes.checkoutButton}
              style={{
                ...(menuData?.location_info?.ordering_system_primary_color ? { backgroundColor: menuData.location_info.ordering_system_primary_color } : {}),
                ...(menuData?.location_info?.ordering_system_secondary_color ? { color: menuData.location_info.ordering_system_secondary_color } : {}),
              }}
              onClick={handleOpenCheckout}>
              Next: Checkout
            </Button>
          </Box>
        </>
      )}
      {/* Checkout Modal */}
      <Dialog open={checkoutOpen} onClose={handleCloseCheckout} maxWidth={'sm'} fullWidth aria-labelledby="checkout-dialog" disableScrollLock>
        {/*@ts-ignore*/}
        <KioskCheckout
          //@ts-ignore
          orderDetails={orderDetails}
          //@ts-ignore
          dispatch={dispatch}
          totalPrice={totalPrice}
          onClose={handleCloseCheckout}
          cartItems={cartItems}
          cart={cart}
          menuData={menuData}
          location_id={location_id}
          organisation_id={organisation_id}
          onOrderCompleted={onOrderCompleted}
        />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="You can't add more of this item. It exceeds the available stock."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default KioskCart;
